import { responsiveBreakpointsPx } from '@/src/constants';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface StickyCtaOffsetMeasurements {
    announcementBannerHeight: number;
    isMobile: boolean;
    isSearchOpen: boolean;
    isSticking: boolean;
    isInFlow: boolean;
    isGlobalNavBehavior: boolean;
    scrollY: number;
    navHeight: number;
    stickyBarHeight: number;
    navVisibleBottom?: number;
    isStickyDismissed?: boolean;
}

export interface StickyCtaOffsets {
    top: number;
    barTop: number;
    barReserve: number;
    navReserve: number;
    channelGuideTop: number;
}

export interface StickyCtaCssOffsets {
    '--sticky-cta-top': number;
    '--sticky-bar-top': number;
    '--sticky-bar-reserve': number;
    '--nav-reserve': number;
    '--channel-guide-top': number;
}

export interface StickyCtaElementSize {
    height: number;
}

export interface StickyCtaUpdateOffsetsRef {
    current: () => void;
}

const INITIAL_OFFSETS: StickyCtaCssOffsets = {
    '--sticky-cta-top': 0,
    '--sticky-bar-top': 0,
    '--sticky-bar-reserve': 0,
    '--nav-reserve': 0,
    '--channel-guide-top': 0,
};

const OFFSET_NAMES = Object.keys(INITIAL_OFFSETS) as Array<
    keyof StickyCtaCssOffsets
>;

const COOKIE_BANNER_SELECTOR = '#onetrust-banner-sdk';
const COOKIE_PREFERENCE_CENTER_SELECTOR = '#onetrust-pc-sdk';
const CTA_ON_COOKIE_BANNER_CLASS = 'stickyCtaOnCookieBanner';
const COOKIE_BANNER_HEIGHT_VAR = '--cookie-banner-height';
const COOKIE_BANNER_SETTLE_FRAMES = 15;
const COOKIE_BANNER_SHRINK_TOLERANCE_PX = 1.5;

export function computeStickyOffsets({
    announcementBannerHeight,
    isMobile,
    isSearchOpen,
    isSticking,
    isInFlow,
    isGlobalNavBehavior,
    scrollY,
    navHeight,
    stickyBarHeight,
    navVisibleBottom = 0,
    isStickyDismissed = false,
}: StickyCtaOffsetMeasurements): StickyCtaOffsets {
    const shouldOffsetSticky =
        scrollY < (announcementBannerHeight || 10) || isSearchOpen;

    const stickyCtaTop = shouldOffsetSticky
        ? announcementBannerHeight
        : 0;

    const stickyH = isSticking ? stickyBarHeight : 0;

    const stickyBarTop = isMobile || isInFlow ? 0 : stickyH;

    const stickyBarReserve = isMobile ? 0 : stickyBarHeight;

    const isAtPageTop = scrollY < 10;

    let channelGuideTop: number;

    if (isMobile) {
        if (isGlobalNavBehavior && !isSticking) {
            channelGuideTop = navHeight;
        } else {
            channelGuideTop = 0;
        }
    } else if (isGlobalNavBehavior) {
        if (isSticking) {
            channelGuideTop = stickyH;
        } else {
            channelGuideTop = navHeight;
        }
    } else {
        channelGuideTop = isAtPageTop
            ? stickyH + navHeight
            : stickyH;
    }
    if (isStickyDismissed && navVisibleBottom > 0) {
        channelGuideTop = navVisibleBottom;
    }

    return {
        top: stickyCtaTop,
        barTop: stickyBarTop,
        barReserve: stickyBarReserve,
        navReserve: navHeight,
        channelGuideTop,
    };
}

interface UseStickyCtaOffsetsProps {
    scrollUpBehavior: string;
    isTopLevelSticky: boolean;
    triggerRef: React.RefObject<HTMLDivElement | null>;
    navSize: StickyCtaElementSize;
    stickyBarSize: StickyCtaElementSize;
    updateOffsetsRef: StickyCtaUpdateOffsetsRef;
}

function isShown(element: Element | null): boolean {
    if (!element) return false;

    const { display, visibility } = getComputedStyle(element);

    return display !== 'none' && visibility !== 'hidden';
}

function measureCookieBannerHeight(): number {
    const banner = document.querySelector<HTMLElement>(
        COOKIE_BANNER_SELECTOR,
    );

    if (!banner || !isShown(banner)) return 0;

    if (parseFloat(banner.style.opacity) < 0.5) return 0;

    const rect = banner.getBoundingClientRect();

    if (rect.bottom < window.innerHeight - 1) return 0;

    return Math.max(
        0,
        Math.round((window.innerHeight - rect.top) * 100) / 100,
    );
}

function isCookieBannerAnimating(): boolean {
    const banner = document.querySelector<HTMLElement>(
        COOKIE_BANNER_SELECTOR,
    );
    if (!banner || typeof banner.getAnimations !== 'function') {
        return false;
    }

    return banner
        .getAnimations()
        .some((animation) => animation.playState === 'running');
}

export function useStickyCtaOffsets({
    scrollUpBehavior,
    isTopLevelSticky,
    triggerRef,
    navSize,
    stickyBarSize,
    updateOffsetsRef,
}: UseStickyCtaOffsetsProps): StickyCtaCssOffsets {
    const [offsets, setOffsets] =
        useState<StickyCtaCssOffsets>(INITIAL_OFFSETS);

    const heldCookieBannerHRef = useRef(0);
    const trackCookieBannerSlideRef = useRef<() => void>(() => { });

    const updateOffsets = useCallback(() => {
        const nav = document.querySelector<HTMLElement>(
            '[data-componenttype="Global Nav"]',
        );

        const stickyBar = document.querySelector<HTMLElement>(
            '[class*="stickybar"]',
        );

        const announcementBannerHeight =
            parseInt(
                getComputedStyle(document.documentElement).getPropertyValue(
                    '--announcement-banner-height',
                ),
                10,
            ) || 0;

        const isMobile =
            window.innerWidth < responsiveBreakpointsPx.md;

        const isSearchOpen = nav?.dataset.searchOpen === 'true';

        const top = triggerRef.current?.getBoundingClientRect()?.top;

        const shouldShowStickyDuringSearch =
            isTopLevelSticky || (top !== undefined && top <= -1);

        const isSticking =
            document.body.classList.contains('stickyBarStick') ||
            (isSearchOpen && shouldShowStickyDuringSearch);

        const measuredStickyBarHeight =
            stickyBarSize.height || stickyBar?.offsetHeight || 0;

        const navHeight = navSize.height || nav?.offsetHeight || 0;

        const navRect = nav?.getBoundingClientRect();

        const navVisibleBottom =
            navRect &&
                navRect.bottom > 0 &&
                navRect.top < window.innerHeight
                ? navRect.bottom
                : 0;

        const isStickyDismissed =
            sessionStorage.getItem('stickyCtaDismissed') === '1';

        const isInFlow = stickyBar
            ? getComputedStyle(stickyBar).position === 'sticky'
            : false;

        const computedOffsets = computeStickyOffsets({
            announcementBannerHeight,
            isMobile,
            isSearchOpen,
            isSticking,
            isInFlow,
            isGlobalNavBehavior:
                scrollUpBehavior === 'Global Nav',
            scrollY: window.scrollY,
            navHeight,
            stickyBarHeight: measuredStickyBarHeight,
            navVisibleBottom,
            isStickyDismissed,
        });

        // OneTrust banner positioning applies to the bottom-anchored mobile bar.
        let cookieBannerH = isMobile ? measureCookieBannerHeight() : 0;

        const isCookieBannerSliding =
            isMobile && isCookieBannerAnimating();

        const heldCookieBannerH = heldCookieBannerHRef.current;

        if (
            cookieBannerH > 0 &&
            cookieBannerH < heldCookieBannerH &&
            heldCookieBannerH - cookieBannerH <=
            COOKIE_BANNER_SHRINK_TOLERANCE_PX
        ) {
            cookieBannerH = heldCookieBannerH;
        }

        heldCookieBannerHRef.current = cookieBannerH;

        // Update directly so the sticky bar follows the banner without a React-render delay.
        const root = document.documentElement;

        root.style.setProperty(
            COOKIE_BANNER_HEIGHT_VAR,
            `${cookieBannerH}px`,
        );

        root.classList.toggle(
            CTA_ON_COOKIE_BANNER_CLASS,
            cookieBannerH > 0 &&
            !isShown(
                document.querySelector(
                    COOKIE_PREFERENCE_CENTER_SELECTOR,
                ),
            ),
        );

        if (isCookieBannerSliding) {
            trackCookieBannerSlideRef.current();
        }

        const nextOffsets: StickyCtaCssOffsets = {
            '--sticky-cta-top': computedOffsets.top,
            '--sticky-bar-top': computedOffsets.barTop,
            '--sticky-bar-reserve': computedOffsets.barReserve,
            '--nav-reserve': computedOffsets.navReserve,
            '--channel-guide-top': computedOffsets.channelGuideTop,
        };
        setOffsets((current) => {
            for (const name of OFFSET_NAMES) {
                if (current[name] !== nextOffsets[name]) {
                    return nextOffsets;
                }
            }

            return current;
        });
    }, [
        navSize.height,
        stickyBarSize.height,
        scrollUpBehavior,
        isTopLevelSticky,
        triggerRef,
    ]);

    useEffect(() => {
        let slideFrame = 0;
        let settleFramesLeft = 0;

        function trackCookieBannerSlide() {
            settleFramesLeft = COOKIE_BANNER_SETTLE_FRAMES;

            if (slideFrame) return;

            const step = () => {
                updateOffsets();

                settleFramesLeft = isCookieBannerAnimating()
                    ? COOKIE_BANNER_SETTLE_FRAMES
                    : settleFramesLeft - 1;

                slideFrame =
                    settleFramesLeft > 0
                        ? requestAnimationFrame(step)
                        : 0;
            };

            slideFrame = requestAnimationFrame(step);
        }

        trackCookieBannerSlideRef.current = trackCookieBannerSlide;
        updateOffsetsRef.current = updateOffsets;

        let observedCookieBanner: Element | null = null;

        const cookieBannerResizeObserver =
            typeof ResizeObserver === 'undefined'
                ? null
                : new ResizeObserver(() => updateOffsets());

        function observeCookieBanner() {
            const banner = document.querySelector(COOKIE_BANNER_SELECTOR);

            if (
                !cookieBannerResizeObserver ||
                banner === observedCookieBanner
            ) {
                return;
            }

            if (observedCookieBanner) {
                cookieBannerResizeObserver.unobserve(observedCookieBanner);
            }

            if (banner) {
                cookieBannerResizeObserver.observe(banner, {
                    box: 'border-box',
                });
            }

            observedCookieBanner = banner;
        }

        const initialFrame = requestAnimationFrame(updateOffsets);

        observeCookieBanner();

        const mutationObserver = new MutationObserver(() => {
            observeCookieBanner();
            requestAnimationFrame(updateOffsets);
        });

        mutationObserver.observe(document.body, {
            attributes: true,
            attributeFilter: ['class'],
        });

        mutationObserver.observe(document.documentElement, {
            attributes: true,
            subtree: true,
            childList: true,
        });

        window.addEventListener('scroll', updateOffsets, {
            passive: true,
        });

        window.addEventListener('resize', updateOffsets);

        window.addEventListener(
            'oneTrustConsentChanged',
            updateOffsets,
        );

        return () => {
            cancelAnimationFrame(initialFrame);
            cancelAnimationFrame(slideFrame);

            mutationObserver.disconnect();
            cookieBannerResizeObserver?.disconnect();

            trackCookieBannerSlideRef.current = () => { };

            document.documentElement.classList.remove(
                CTA_ON_COOKIE_BANNER_CLASS,
            );

            document.documentElement.style.removeProperty(
                COOKIE_BANNER_HEIGHT_VAR,
            );

            window.removeEventListener('scroll', updateOffsets);
            window.removeEventListener('resize', updateOffsets);

            window.removeEventListener(
                'oneTrustConsentChanged',
                updateOffsets,
            );
        };
    }, [updateOffsets, updateOffsetsRef]);

    return offsets;
}